import { useState, useEffect } from 'react';
import { Bell, BellOff, CheckCheck, X, Megaphone } from 'lucide-react';
import { useNotificationsStore, useAuthStore } from '../store/useStore';
import { notifications as notificationsApi } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useNotificationAnnouncements } from './AnnouncementDisplay';
import { formatAge } from '../utils/age';

export default function NotificationPanel() {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const user = useAuthStore((state) => state.user)!;
  const {
    notifications,
    unreadCount,
    setNotifications,
    setUnreadCount,
    markAsRead,
    markAllAsRead,
    addNotification,
  } = useNotificationsStore();

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setSocket] = useState<Socket | null>(null);

  // Announcement notifications from the admin announcements system
  const { notifications: announcementNotifications, dismiss: dismissAnnouncement } = useNotificationAnnouncements();

  useEffect(() => {
    if (isOpen && notifications.length === 0) {
      loadNotifications();
    }
  }, [isOpen]);

  // Socket.io connection for real-time notifications
  useEffect(() => {
    const socketConnection = io({
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });
    setSocket(socketConnection);

    // Handle connection errors gracefully
    socketConnection.on('connect_error', (error) => {
      // Silently handle connection errors - don't spam console
      console.debug('Notification socket connection error (will retry):', error.message);
    });

    socketConnection.on('disconnect', (reason) => {
      console.debug('Notification socket disconnected:', reason);
    });

    socketConnection.on('reconnect_failed', () => {
      console.debug('Notification socket reconnection failed - falling back to polling');
    });

    // Listen for real-time notifications
    socketConnection.on(`notification-${user.id}`, (notification) => {
      addNotification({
        id: `temp-${Date.now()}`, // Temporary ID, will be replaced when notifications are reloaded
        type: notification.type,
        content: notification.content,
        read: false,
        createdAt: notification.createdAt,
        fromUser: notification.fromUser,
        share: notification.shareId ? {
          id: notification.shareId,
          article: { id: '', title: '' } // Minimal data for now
        } : undefined,
        comment: notification.commentId ? {
          id: notification.commentId,
          article: { id: notification.articleId || '', title: '' } // Article ID from comment mention
        } : undefined,
      });

      // Show toast notification with appropriate message
      let toastMessage = notification.content;
      if (notification.type === 'comment_mention') {
        toastMessage = `@${notification.fromUser.username} mentioned you`;
      } else if (notification.type === 'comment_on_share') {
        toastMessage = `@${notification.fromUser.username} commented on your share`;
      } else if (notification.type === 'comment_on_thread') {
        toastMessage = `@${notification.fromUser.username} commented on a thread you're following`;
      } else if (notification.type === 'share_mention') {
        toastMessage = `@${notification.fromUser.username} mentioned you in a share`;
      } else if (notification.type === 'collab_invitation') {
        toastMessage = `@${notification.fromUser.username} invited you to collaborate`;
      } else if (notification.type === 'collab_new_section') {
        toastMessage = `@${notification.fromUser.username} added to a collaborative post`;
      } else if (notification.type === 'collab_published') {
        toastMessage = `Collaborative post published!`;
      }

      toast.success(toastMessage, {
        duration: 4000,
      });
    });

    return () => {
      socketConnection.disconnect();
    };
  }, [user.id, addNotification]);

  // Notification polling fallback (every 30 seconds when focused)
  useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    function startPolling() {
      pollInterval = setInterval(() => {
        if (document.hasFocus()) {
          loadNotifications();
        }
      }, 30000); // 30 seconds
    }

    function handleFocus() {
      loadNotifications();
      startPolling();
    }

    function handleBlur() {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    }

    // Start polling immediately
    startPolling();

    // Add focus/blur listeners
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  async function loadNotifications() {
    setLoading(true);
    try {
      const data = await notificationsApi.getAll(token, { limit: 20 });
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }

  async function handleMarkAsRead(id: string) {
    try {
      await notificationsApi.markRead(token, id);
      markAsRead(id);
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  }

  async function handleMarkAllAsRead() {
    try {
      await notificationsApi.markAllRead(token);
      markAllAsRead();
      // Also dismiss all announcement notifications
      for (const announcement of announcementNotifications) {
        await dismissAnnouncement(announcement.id);
      }
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all notifications as read');
    }
  }

  function handleNotificationClick(notification: any) {
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.type === 'welcome') {
      navigate('/discover');
    } else if (notification.type === 'onboarding_tools') {
      navigate('/settings');
    } else if (notification.type === 'onboarding_pdf') {
      navigate('/export');
    } else if (notification.type === 'keyboard_shortcuts') {
      navigate('/settings');
    } else if (notification.share) {
      navigate(`/article/${notification.share.article.id}`);
    } else if (notification.comment) {
      navigate(`/article/${notification.comment.article.id}`);
    } else if (notification.type === 'comment_mention' && notification.comment) {
      navigate(`/article/${notification.comment.article.id}`);
    } else if (notification.type === 'comment_on_thread' && notification.comment) {
      navigate(`/article/${notification.comment.article.id}`);
    } else if (notification.type === 'comment_on_share' && notification.comment) {
      navigate(`/article/${notification.comment.article.id}`);
    } else if (notification.type === 'digest') {
      navigate('/digest');
    }

    setIsOpen(false);
  }

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-ink-muted hover:text-ink hover:bg-surface-sunken"
        title="Notifications"
      >
        <Bell className={`w-5 h-5 ${(unreadCount + announcementNotifications.length) > 0 ? 'text-accent-600' : ''}`} />
        {(unreadCount + announcementNotifications.length) > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-signal text-paper font-mono text-[10px] leading-none min-w-[1.125rem] h-[1.125rem] px-1 flex items-center justify-center">
            {(unreadCount + announcementNotifications.length) > 9 ? '9+' : (unreadCount + announcementNotifications.length)}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="absolute top-full mt-2 w-80 bg-canvas border border-ink z-50 max-h-96 overflow-hidden modal-animate" style={{ right: '-5rem' }}>
            {/* Header */}
            <div className="flex items-center justify-between h-10 pl-4 pr-2 border-b border-edge">
              <span className="kicker text-ink">Notifications{unreadCount > 0 && <> · {unreadCount} unread</>}</span>
              <div className="flex items-center gap-1">
                {(unreadCount > 0 || announcementNotifications.length > 0) && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="kicker hover:text-ink flex items-center gap-1 px-1"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-ink-muted hover:text-ink"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="px-4 py-8 kicker">Loading…</div>
              ) : notifications.length === 0 && announcementNotifications.length === 0 ? (
                <div className="px-4 py-8">
                  <div className="kicker mb-1 flex items-center gap-2"><BellOff className="w-3.5 h-3.5" /> Empty</div>
                  <p className="text-sm text-ink-secondary">No notifications yet.</p>
                </div>
              ) : (
                <div>
                  {/* Announcement Notifications */}
                  {announcementNotifications.map((announcement) => (
                    <div
                      key={`announcement-${announcement.id}`}
                      className="p-4 border-b border-edge-subtle border-l-2 border-l-accent bg-surface-sunken"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-ink text-paper flex items-center justify-center">
                            <Megaphone className="w-4 h-4" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="kicker">Announcement · {formatAge(announcement.createdAt)}</div>
                          <p className="text-sm font-semibold text-ink">
                            {announcement.title}
                          </p>
                          <p className="text-xs text-ink-secondary mt-0.5 line-clamp-2">
                            {announcement.content.substring(0, 100)}...
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissAnnouncement(announcement.id);
                          }}
                          className="text-ink-muted hover:text-ink p-1"
                          title="Dismiss"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 cursor-pointer hover:bg-surface-sunken border-b border-edge-subtle border-l-2 ${
                        !notification.read ? 'border-l-accent' : 'border-l-transparent'
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          {notification.fromUser.avatar ? (
                            <img
                              src={notification.fromUser.avatar}
                              alt=""
                              className="w-8 h-8 rounded-full object-cover avatar-duotone"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-ink text-paper rounded-full flex items-center justify-center">
                              <span className="text-sm font-semibold">
                                {notification.fromUser.displayName?.[0] ||
                                 notification.fromUser.username[0].toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-ink">
                            {notification.content}
                          </p>
                          <div className="flex items-center gap-3 mt-1 kicker">
                            <span>{formatAge(notification.createdAt)}</span>
                            {!notification.read && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAsRead(notification.id);
                                }}
                                className="hover:text-ink"
                              >
                                Mark read
                              </button>
                            )}
                          </div>
                        </div>

                        {!notification.read && (
                          <span className="dot dot-accent mt-2" aria-label="unread" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}